
import * as React from 'react';
import { createRef, useEffect, useState } from 'react';
import ProfileNavbar, { NavbarItemProps } from "../../components/profileNavbar";
import avatar from "../../assets/images/avatar.jpg"
import { Roles } from '../../components/roles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../components/ui/Dialog';
import Button from '../../components/ui/Button';
import { Check, X } from 'lucide-react';
import { connect, useSelector } from 'react-redux';
import Cropper from 'react-easy-crop';
import { getUser, getUserManagers, updateProfileImage } from '../../api/General/account';
import { useTranslation } from "react-i18next";
import { setSelectedUser, setSelectedUserManagers, setImageUrl, setUserFaceImage } from '../../store/redux/actions/userActionCreator';
import getCroppedImg from '../../utils/cropImage';
import { ProfileImageInput } from '../../models/general';
import { getFullUrl } from '../../utils/url';
import { useProfileTabConfig } from '../../hooks/useProfileTabConfig';
import { useFileUploadLimits } from '../../hooks/useFileUploadLimits';
import ResetPasswordDialog from '../EmployeesCycle/General/ResetPasswordDialog';
import AppIcon from '../../components/AppIcon';


type Props = {
  profile?: boolean,
  children?: any,
  prevUrl?: any,
  userId?:string,
  role?: any
  imageUrl?: string
  selectedUser?: any
  selectedUserManagers?: any
  setSelectedUser?: any
  setSelectedUserManagers?: any
  setImageUrl?: (url: string) => void
  setUserFaceImage?: (img: any) => void
  companyPrimaryColor?: string
}

const UserProfile =  (props: Props) => {
  const { t } = useTranslation('common');
  const fileInputRef = createRef<HTMLInputElement>();

  const baseUrl = window.location.origin;
  const userId = props.userId
  const { id } = useParams()
  const previousLocation = props.prevUrl
  const splittedUrl = previousLocation?.split(baseUrl) != undefined ? previousLocation?.split(baseUrl) : baseUrl + "/apps"
  const url = splittedUrl.length == 1 ? splittedUrl[0] : splittedUrl[1]
  const companyName = "Numow"
  const logo = avatar
  const loading = props.selectedUser == undefined || props.selectedUser.id != id

  const isViewingAnotherUser = id != undefined && id != userId;
  const isHr = Roles.HR.includes(props.role);
  const isManagerOfEmployee = (props.selectedUserManagers ?? []).some((s: any) => String(s?.id) === String(userId));

  const isAdmin = Roles.ADMIN.includes(props.role);
  const canAccessSkillsTab = !isViewingAnotherUser || !isAdmin;
  const canAccessPayrollTab = !isViewingAnotherUser || isHr;
  const canAccessAttendanceTab = !isViewingAnotherUser || isHr || isManagerOfEmployee;
  const hideDocumentsTab = isAdmin && isViewingAnotherUser;

  // Profile tab module config (operator-controlled per company)
  const { isTabEnabled } = useProfileTabConfig();
  const { validateFileSize, getMaxSizeMB } = useFileUploadLimits();

  // User can edit their own image, or HR/Admin can edit others
  const canEditImage = !isViewingAnotherUser || isHr || isAdmin;

  // Image upload state
  const [showImagePreviewDialog, setShowImagePreviewDialog] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

  const isOperator = Roles.OPERATOR.includes(props.role);
  const canResetTargetPassword = isViewingAnotherUser && (isHr || isAdmin || isOperator);

  // Auto-dismiss success toast
  useEffect(() => {
    if (!successSnackbar) return;
    const timer = setTimeout(() => setSuccessSnackbar(false), 3000);
    return () => clearTimeout(timer);
  }, [successSnackbar]);

  const validateImage = (file: File) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowed.includes(file.type)) { setUploadError(t('Unsupported file type. Please use JPG, PNG, or WEBP.')); return false; }
    const { valid, maxMB } = validateFileSize(file, 'profilePhotoMaxSizeMB');
    if (!valid) { setUploadError(t('fileSizeExceedsLimit', { name: file.name, maxMB })); return false; }
    setUploadError(null);
    return true;
  };

  const handleImageChange = (e: React.FormEvent<HTMLInputElement>) => {
    const f = (e.target as any).files?.[0];
    if (!f) return;
    if (!validateImage(f)) return;
    setPendingFile(f);
    setPreviewImageUrl(URL.createObjectURL(f));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setShowImagePreviewDialog(true);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCancelImage = () => {
    setShowImagePreviewDialog(false);
    setPendingFile(null);
    setPreviewImageUrl(null);
  };

  const handleConfirmImage = async () => {
    if (!pendingFile || !croppedAreaPixels) return;
    try {
      setUploadingImage(true);
      const blob = await getCroppedImg(previewImageUrl!, croppedAreaPixels, 'image/jpeg', 0.92);
      const baseName = pendingFile.name.replace(/\.(png|webp|jpeg|jpg)$/i, '');
      const croppedFile = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
      const res = await updateProfileImage({ image: croppedFile } as ProfileImageInput, id ?? userId);
      if (res.payload?.imageUrl) {
        // Only update the logged-in user's Redux state when editing own profile
        if (!isViewingAnotherUser) {
          props.setImageUrl?.(res.payload.imageUrl);
          const face = res.payload.userFaceImage ?? { path: res.payload.imageUrl };
          props.setUserFaceImage?.(face);
        }
        // Update selected user in Redux (for the profile page)
        props.setSelectedUser?.({ ...props.selectedUser, imageUrl: res.payload.imageUrl });
        setSuccessSnackbar(true);
      }
    } catch (err) {
      console.error(err);
      setUploadError(t('Failed to update profile image. Please try again.'));
    } finally {
      setShowImagePreviewDialog(false);
      setPendingFile(null);
      setPreviewImageUrl(null);
      setUploadingImage(false);
    }
  };

  const onCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
      fetchData();
  }, []);

  const fetchData = async() => {
    if (props.selectedUser == undefined || props.selectedUser.id != id) {
      const res = await getUser(id != undefined ? id : userId as string)
      props.setSelectedUser(res.payload)
      // Sync logged-in user's imageUrl from fresh API data when viewing own profile
      if (!isViewingAnotherUser && res.payload?.imageUrl && res.payload.imageUrl !== props.imageUrl) {
        props.setImageUrl?.(res.payload.imageUrl);
      }
      const selectedUserManagers = await getUserManagers(id != undefined ? id : (props.userId as string));
      props.setSelectedUserManagers(selectedUserManagers.payload);
    }
  }

  const navBarSideItems: NavbarItemProps[] = canResetTargetPassword
    ? [
        {
          id: "resetPassword",
          content: (
            <span className="inline-flex items-center gap-1.5">
              <AppIcon name="KeyRound" size={14} />
              {t("Reset password")}
            </span>
          ),
          onClick: () => setResetPasswordOpen(true),
        },
      ]
    : [];
  const navBarSideItemsUser = [
    {
      // Direct users to backend Identity UI change password page instead of frontend React page
      id: "changePassword", content: t("CHANGE PASSWORD"), to: "/Account/Manage/ChangePassword"
    },
  ]
  const navbarItems: NavbarItemProps[] = [
    {
      id: "personal", content: t("PERSONAL") || "PERSONAL", to: "/profile/" + id + "/personal"
    },
    // The DOCUMENTS tab now also hosts the self-service "On Demand Documents" section
    // (own profile only), so there is no separate My Documents tab.
    ...(!hideDocumentsTab ? [{
      id: "documents", content: t("DOCUMENTS") || "DOCUMENTS", to: "/profile/" + id + "/documents"
    }] : []),
    ...(canAccessSkillsTab ? [{
      id: "skills", content: t("SKILLS") || "SKILLS", to: "/profile/" + id + "/skills"
    }] : []),
    ...(canAccessPayrollTab ? [
      {
        id: "payroll", content: t("PAYROLL") || "PAYROLL", to: "/profile/" + id + "/payroll/active-payroll-month"
      },
    ] : []),
    ...(canAccessAttendanceTab ? [
      ...(isHr ? [{
        id: "attendance", content: t("ATTENDANCE") || "ATTENDANCE", to: "/profile/" + id + "/attendance/rules"
      }] : []),
      {
        id: "leaves", content: t("LEAVES") || "LEAVES", to: "/profile/" + id + "/leaves"
      },
    ] : []),
   ...(Roles.HR.includes(props.role) ? [
    {
      id: "map", content: t("WORKPLACE MAP")||"WORKPLACE MAP", to: "/profile/" + id + "/map"
    },
    ] : []) ,
    {
      id: "assets", content: t("ASSETS") || "ASSETS", to: "/profile/" + id + "/assets"
    },
    {
      id: "businessRoles", content: t("BUSINESS ROLES") || "BUSINESS ROLES", to: "/profile/" + id + "/business-roles"
    },
    {
      id: "dependents", content: t("DEPENDENTS") || "DEPENDENTS", to: "/profile/" + id + "/dependents"
    },
  ]

  // Apply module config filtering (additional layer on top of role-based checks)
  const filteredNavbarItems = navbarItems.filter(item => isTabEnabled(item.id));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="user-profile-shell">
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/*"
        capture="user"
        onChange={handleImageChange}
      />

      <ProfileNavbar
        title={companyName ?? "Mowazaf"}
        logo={logo}
        imageUrl={getFullUrl(props.selectedUser?.imageUrl) || undefined}
        items={filteredNavbarItems}
        primaryColor={props.companyPrimaryColor || "var(--mowazaf-primary)"}
        sideItems={userId == id ? navBarSideItemsUser : navBarSideItems}
        fullName={props.selectedUser?.fullName}
        jobTitle={props.selectedUser?.title}
        department={props.selectedUser?.department?.name}
        onImageChange={triggerImageUpload}
        canEditImage={canEditImage}
      />
      <main className="user-profile-content">
        {props.children}
      </main>

      {canResetTargetPassword && id && (
        <ResetPasswordDialog
          open={resetPasswordOpen}
          onOpenChange={setResetPasswordOpen}
          userId={id}
          userDisplayName={props.selectedUser?.fullName || props.selectedUser?.email || id}
        />
      )}

      {/* Image crop dialog */}
      <Dialog open={showImagePreviewDialog} onOpenChange={(open) => { if (!open) handleCancelImage(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('Crop Profile Picture')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {previewImageUrl && (
              <div className="relative w-full h-[300px] bg-muted rounded-lg">
                <Cropper
                  image={previewImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
            )}
            <div className="mt-2 px-2">
              <p className="text-sm text-muted-foreground mb-1">{t('Zoom')}</p>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>
            {uploadError && (
              <p className="text-sm text-destructive mt-1">{uploadError}</p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelImage} disabled={uploadingImage}>{t('Cancel')}</Button>
            <Button
              variant="default"
              onClick={handleConfirmImage}
              disabled={uploadingImage}
              loading={uploadingImage}
            >
              {uploadingImage ? t('Uploading...') : t('Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success toast */}
      {successSnackbar && (
        <div className="fixed top-4 inset-x-0 z-50 flex justify-center pointer-events-none">
          <div role="alert" className="pointer-events-auto flex items-center gap-2 rounded-lg border border-success/20 bg-success/10 px-4 py-3 text-sm text-success shadow-lg animate-in fade-in-0 slide-in-from-top-2 duration-300">
            <Check className="w-4 h-4" />
            <span>{t('Profile picture updated successfully')}</span>
            <button onClick={() => setSuccessSnackbar(false)} aria-label={t('Dismiss')} className="ms-2 p-0.5 rounded hover:bg-success/20 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

};

const mapStateToProps = (state: any) => {
  return {
    prevUrl: state.user.prevUrl,
    userId: state.user.userId,
    role: state.user.role,
    imageUrl: state.user.imageUrl,
    selectedUser: state.user.selectedUser,
    selectedUserManagers: state.user.selectedUserManagers,
    companyPrimaryColor: state.user.companyPrimaryColor
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    setSelectedUser: (user: any) => dispatch(setSelectedUser(user)),
    setSelectedUserManagers: (managers: any) => dispatch(setSelectedUserManagers(managers)),
    setImageUrl: (url: string) => dispatch(setImageUrl(url)),
    setUserFaceImage: (img: any) => dispatch(setUserFaceImage(img))
  }
}

export default connect(mapStateToProps,mapDispatchToProps)(UserProfile)

